import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import logo1 from "../images/logo1.jpg";
import logo2 from "../images/logo2.jpg";

/* ===== Helpers: تحويل الأرقام العربية ===== */
const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
function toArabicDigits(input) {
  return String(input ?? "")
    .replace(/\d/g, (d) => AR_DIGITS[d]);
}

/* ===== API ===== */
const API_BASE = "https://hassen.sstli.com/api/index.php";
async function apiPost(action, payload) {
  const res = await fetch(`${API_BASE}?action=${encodeURIComponent(action)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    mode: "cors",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* عناوين المقررات */
const COURSE_TITLES = {
  cloud: "الحوسبة السحابية",
  cyber: "الجرائم الإلكترونية",
  hrpay: "إدارة نظم الأجور والتعويضات",
};

/* ربط المقررات بالدبلوم + المستوى */
const COURSE_CATALOG = [
  // الأمن السيبراني – المستوى السادس
  { key: "cloud", diplomaIncludes: "الأمن السيبراني", levelEquals: "السادس" },
  { key: "cyber", diplomaIncludes: "الأمن السيبراني", levelEquals: "السادس" },
  // الموارد البشرية – المستوى الخامس
  { key: "hrpay", diplomaIncludes: "الموارد البشرية", levelEquals: "الخامس" },
];

/* كود المقرر من (الدبلوم+المستوى+المقرر) */
function deriveCourseCode(diplomaName, levelName, courseKey) {
  const dipMap = [
    { k: "الموارد البشرية", v: "HR" },
    { k: "إدارة الأعمال", v: "BA" },
    { k: "القانون", v: "LAW" },
    { k: "الأمن السيبراني", v: "CYBSEC" },
  ];
  const lvlMap = [
    { k: "الأول", v: "L1" }, { k: "الثاني", v: "L2" }, { k: "الثالث", v: "L3" },
    { k: "الرابع", v: "L4" }, { k: "الخامس", v: "L5" }, { k: "السادس", v: "L6" },
  ];
  const courseMap = { cloud: "CLD", cyber: "CYB", hrpay: "HRP" };
  const dip = (dipMap.find((x) => diplomaName?.includes(x.k))?.v) || "GEN";
  const lvl = (lvlMap.find((x) => levelName?.includes(x.k))?.v) || "LX";
  const crs = courseMap[courseKey] || "CRS";
  return `${dip}-${lvl}-${crs}`;
}

/* === عنصر صندوق الدرجة === */
const ScoreBox = ({ max = 30, className = "" }) => (
  <div
    className={`score-box ${className}`}
    aria-label={`درجة من ${toArabicDigits(max)}`}
  >
    <div className="score-top" />
    <div className="score-bottom">{toArabicDigits(max)}</div>
  </div>
);

/* الهيدر */
function ExamHeader({
  instituteName,
  diploma,
  level,
  courseTitle,
  courseCode,
  examGrade,
  examDuration,
}) {
  // عرض الأرقام عربية
  const displayExamGrade = examGrade != null ? toArabicDigits(examGrade) : "—";
  const displayExamDuration = examDuration ? toArabicDigits(examDuration) : "—";

  return (
    <header className="exam-header">
      <div className="logos-row">
        <img className="logo left" src={logo1} alt="Left Logo" />
        <img className="logo right" src={logo2} alt="Right Logo" />
      </div>

      <div className="info-grid">
        <div className="info-card">
          <div className="info-row">
            <span className="label">اسم المتدرب:</span>
            <span className="value">..................................</span>
          </div>
          <div className="info-row">
            <span className="label">رقم الهوية:</span>
            <span className="value">..................................</span>
          </div>
          <div className="info-row">
            <span className="label">الدبلوم:</span>
            <span className="value">{diploma}</span>
          </div>
          <div className="info-row">
            <span className="label">المستوى:</span>
            <span className="value">{level}</span>
          </div>
        </div>

        <div className="info-card">
          <div className="info-row">
            <span className="label">المقرر:</span>
            <span className="value">{courseTitle}</span>
          </div>
          <div className="info-row">
            <span className="label">كود المقرر:</span>
            <span className="value">{courseCode}</span>
          </div>
          <div className="info-row">
            <span className="label">درجة الاختبار:</span>
            <span className="value">({displayExamGrade}) /</span>
          </div>
          <div className="info-row">
            <span className="label">مدة الاختبار:</span>
            <span className="value">{displayExamDuration}</span>
          </div>
        </div>
      </div>

      <div className="header-divider" />
    </header>
  );
}

/* سطر سؤال واحد (صح/خطأ) */
const OneLineTF = ({ i, text, showScore }) => (
  <div className="one-line">
    <span className="qindex">{toArabicDigits(i)} - </span>
    <span className="qtext">{text}</span>
    {showScore && <ScoreBox className="score-inline tf" />}
    <span className="tf-inline">(   )</span>
  </div>
);

/* سؤال اختياري */
const OneLineMCQ = ({ i, q, opts, showScore }) => (
  <div className="mcq-block">
    <div className="one-line">
      <span className="qindex">{toArabicDigits(i)} - </span>
      <span className="qtext">{q}</span>
    </div>
    <div className="mcq-options-row">
      {opts.map((o, j) => (
        <span key={j} className="mcq-chip">☐ {o}</span>
      ))}
      {showScore && <ScoreBox className="score-inline mcq" />}
    </div>
  </div>
);

/* ورقة الأسئلة */
function ExamPaperPerStudent({
  student,
  courseKey,
  examMeta,
  tfList,
  mcqList,
  sectionOrder, // ترتيب الأقسام
}) {
  const courseCode = deriveCourseCode(
    student.diplomName,
    student.levelName,
    courseKey
  );
  const tfMid = Math.ceil((tfList?.length || 0) / 2) || 1;
  const mcqMid = Math.ceil((mcqList?.length || 0) / 2) || 1;

  // لو TF = 10 أو أكتر ومش أقل من MCQ، وسّع المسافات بين أسطر TF فقط
  const tfRoomy =
    (tfList?.length || 0) >= 10 &&
    (tfList?.length || 0) >= (mcqList?.length || 0);

  // ترتيب ديناميكي
  const autoTFFirst = (tfList?.length || 0) >= (mcqList?.length || 0);
  const showTFFirst =
    sectionOrder === "tf-first" ||
    (sectionOrder === "auto" && autoTFFirst);
  const blocks = [];

  const renderTF = () => (
    tfList?.length > 0 && (
      <>
        <div className="section-title-center">
          <span>السؤال الأول: الصح والخطأ</span>
        </div>
        <div className="tf-section">
          {tfList.map((t, i) => (
            <OneLineTF
              key={`tf-${i}`}
              i={i + 1}
              text={t}
              showScore={i + 1 === tfMid}
            />
          ))}
        </div>
      </>
    )
  );

  const renderMCQ = () => (
    mcqList?.length > 0 && (
      <>
        <div className="section-title-center">
          <span>السؤال الثاني: اختر الإجابة الصحيحة</span>
        </div>
        {mcqList.map((m, i) => (
          <OneLineMCQ
            key={`mcq-${i}`}
            i={i + 1}
            q={m.q}
            opts={m.a}
            showScore={i + 1 === mcqMid}
          />
        ))}
      </>
    )
  );

  if (showTFFirst) {
    blocks.push(renderTF());
    blocks.push(renderMCQ());
  } else {
    blocks.push(renderMCQ());
    blocks.push(renderTF());
  }

  return (
    <div className={`exam-paper page ${tfRoomy ? "tf-density-roomy" : ""}`}>
      <ExamHeader
        instituteName="المعهد السعودي المتخصص العالي للتدريب"
        diploma={student.diplomName}
        level={student.levelName}
        courseTitle={COURSE_TITLES[courseKey] || "—"}
        courseCode={courseCode}
        examGrade={examMeta?.total_grade ?? "—"}
        examDuration={
          examMeta?.duration_minutes ? `${examMeta.duration_minutes} دقيقة` : "—"
        }
      />

      {blocks}

      <div className="footer">
        <span className="page-num" />
      </div>
      <div className="end-note">
        انتهت الأسئلة. مع تمنياتنا لكم بالتوفيق والنجاح.
      </div>
      <div className="footer">
        <span className="page-num" />
      </div>
    </div>
  );
}

/* الفلاتر */
function Filters({ data, selected, setSelected, sectionOrder, setSectionOrder }) {
  const diplomas = useMemo(
    () => Array.from(new Set(data.map((s) => s.diplomName))).sort(),
    [data]
  );
  const levels = useMemo(() => {
    const filtered = selected.diploma
      ? data.filter((s) => s.diplomName === selected.diploma)
      : data;
    return Array.from(new Set(filtered.map((s) => s.levelName))).sort();
  }, [data, selected.diploma]);

  // احسب المقررات المتاحة حسب الدبلوم والمستوى
  const availableCourses = useMemo(() => {
    if (!selected.diploma || !selected.level) return [];
    return COURSE_CATALOG
      .filter(
        c =>
          selected.diploma.includes(c.diplomaIncludes) &&
          selected.level === c.levelEquals
      )
      .map(c => c.key);
  }, [selected.diploma, selected.level]);

  // لو المقرر المختار مش ضمن المتاح، صفّره
  useEffect(() => {
    if (selected.course && !availableCourses.includes(selected.course)) {
      setSelected(p => ({ ...p, course: "" }));
    }
  }, [availableCourses, selected.course, setSelected]);

  return (
    <div className="filters no-print">
      <div className="filter">
        <label>الدبلوم</label>
        <select
          value={selected.diploma}
          onChange={(e) =>
            setSelected({ diploma: e.target.value || "", level: "", course: "" })
          }
        >
          <option value="">— اختر الدبلوم —</option>
          {diplomas.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <div className="filter">
        <label>المستوى</label>
        <select
          value={selected.level}
          onChange={(e) =>
            setSelected((p) => ({ ...p, level: e.target.value || "", course: "" }))
          }
          disabled={!selected.diploma}
        >
          <option value="">— اختر المستوى —</option>
          {levels.map((lv) => (
            <option key={lv} value={lv}>
              {lv}
            </option>
          ))}
        </select>
      </div>

      <div className="filter">
        <label>المقرر</label>
        <select
          value={selected.course}
          onChange={(e) => setSelected((p) => ({ ...p, course: e.target.value }))}
          disabled={!selected.level}
        >
          <option value="">— اختر المقرر —</option>
          {availableCourses.includes("cloud") && (
            <option value="cloud">مقرر {COURSE_TITLES.cloud}</option>
          )}
          {availableCourses.includes("cyber") && (
            <option value="cyber">مقرر {COURSE_TITLES.cyber}</option>
          )}
          {availableCourses.includes("hrpay") && (
            <option value="hrpay">مقرر {COURSE_TITLES.hrpay}</option>
          )}
        </select>
      </div>

      {/* اختيار ترتيب الأقسام قبل الطباعة */}
      <div className="filter">
        <label>ترتيب الأسئلة</label>
        <select
          value={sectionOrder}
          onChange={(e) => setSectionOrder(e.target.value)}
          title="اختر ترتيب الأقسام في ورقة الأسئلة"
        >
          <option value="tf-first">الصح/الخطأ أولًا</option>
          <option value="mcq-first">الاختياري أولًا</option>
          <option value="auto">تلقائي</option>
        </select>
      </div>
    </div>
  );
}

/* التطبيق */
export default function PrintExamPage() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState({ diploma: "", level: "", course: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sectionOrder, setSectionOrder] = useState("tf-first"); // ترتيب الأقسام

  const [examMeta, setExamMeta] = useState(null);
  const [tfList, setTfList] = useState([]);
  const [mcqList, setMcqList] = useState([]);
  const [examStatus, setExamStatus] = useState("idle");

  const STUDENTS_API =
    "https://498c26acc261.ngrok-free.app/api/StudentStudyInfo/by-branch/47fa287e-2517-4424-b58f-bd67f9a042d9";

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(STUDENTS_API, {
        mode: "cors",
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Content-Type': 'application/json'
        }
      });
      const json = await res.json();
      setStudents(Array.isArray(json) ? json : []);
    } catch (e) {
      setErr("تعذر تحميل البيانات. تأكد من الـ API و CORS.");
      setStudents([
        {
          studentName: "سعيد علي صالح الزهراني",
          nationalId: "1063203838",
          diplomName: "دبلوم الأمن السيبراني",
          levelName: "السادس",
        },
        {
          studentName: "هويدي خلف عبدالهادي الدوسري",
          nationalId: "1094831052",
          diplomName: "دبلوم الموارد البشرية",
          levelName: "الخامس",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };
  fetchData();
}, []);

  const filtered = useMemo(() => {
    return students.filter((s) => {
      if (selected.diploma && s.diplomName !== selected.diploma) return false;
      if (selected.level && s.levelName !== selected.level) return false;
      return true;
    });
  }, [students, selected.diploma, selected.level]);

  useEffect(() => {
    setExamMeta(null);
    setTfList([]);
    setMcqList([]);
    setExamStatus("idle");

    const { diploma, level, course } = selected;
    if (!diploma || !level || !course) return;

    const courseMap = { cloud: "CLD", cyber: "CYB", hrpay: "HRP" };
    const course_code = courseMap[course] || course;

    (async () => {
      try {
        setExamStatus("loading");
        const res = await apiPost("get_exam_by_meta", {
          diploma_name: diploma,
          level_name: level,
          course_code,
        });

        if (res.ok && res.exam) {
          setExamMeta({
            id: res.exam.id,
            title: res.exam.title || "—",
            total_grade: Number(res.exam.total_grade ?? 60),
            duration_minutes: Number(res.exam.duration_minutes ?? 60),
          });

          const questions = Array.isArray(res.questions) ? res.questions : [];
          const tf = questions
            .filter((q) => (q.type || "").toUpperCase() === "TF")
            .map((q) => q.text);

          const mcq = questions
            .filter((q) => (q.type || "").toUpperCase() === "MCQ")
            .map((q) => ({
              q: q.text,
              a: (q.options || [])
                .map((o) => o.opt_text || o.text)
                .filter(Boolean),
            }));

          setTfList(tf);
          setMcqList(mcq);
          setExamStatus("found");
        } else {
          setExamStatus("not_found");
        }
      } catch (e) {
        setExamStatus("error");
      }
    })();
  }, [selected.diploma, selected.level, selected.course]);

  const canPrint = selected.diploma && selected.level && selected.course;
  const handlePrint = () => window.print();

  const today = new Date().toLocaleDateString("ar-SA");
  const eligibleText = toArabicDigits(filtered.length);

  return (
    <div className="App">
      <div className="app-header no-print">
        <div className="header-actions">
          <Filters
            data={students}
            selected={selected}
            setSelected={setSelected}
            sectionOrder={sectionOrder}
            setSectionOrder={setSectionOrder}
          />
          <div className="stats">
            {loading ? "جاري التحميل..." : `المستحقون: ${eligibleText}`}
            {err && <span className="err"> — {err}</span>}
          </div>
          <button
            className="print-button"
            onClick={handlePrint}
            disabled={!canPrint || filtered.length === 0 || examStatus !== "found"}
            title={
              !canPrint
                ? "اختر الدبلوم والمستوى والمقرر"
                : examStatus !== "found"
                ? "لا يوجد امتحان مطابق للطباعة"
                : ""
            }
          >
            🖨️ طباعة الأوراق
          </button>
        </div>
      </div>

      <div className="print-area">
        {canPrint &&
          examStatus === "found" &&
          filtered.map((st) => (
            <ExamPaperPerStudent
              key={st.nationalId}
              student={st}
              courseKey={selected.course}
              examMeta={examMeta}
              tfList={tfList}
              mcqList={mcqList}
              sectionOrder={sectionOrder}
            />
          ))}

        {canPrint && examStatus === "loading" && (
          <div className="exam-paper hint no-print">جاري تحميل الامتحان…</div>
        )}

        {canPrint && examStatus === "not_found" && (
          <div className="exam-paper hint no-print">
            لا يوجد امتحان مسجل لهذه التركيبة (الدبلوم/المستوى/المقرر).
          </div>
        )}

        {canPrint && examStatus === "error" && (
          <div className="exam-paper hint no-print">
            حدث خطأ أثناء جلب الامتحان. تأكد من الـ API.
          </div>
        )}
      </div>

      {!canPrint && (
        <div className="exam-paper hint no-print">
          اختَر الدبلوم ثم المستوى ثم المقرر لعرض الأوراق وتجهيز الطباعة.
        </div>
      )}
    </div>
  );
}
